export interface Database {
  public: {
    Tables: {
      countdowns: {
        Row: {
          id: string;
          user_id: string;
          name: string;
          event_date: string;
          emoji: string;
          color: string;
          category: string;
          is_recurring: boolean;
          is_featured: boolean;
          share_id: string | null;
          remind_1_day: boolean;
          remind_1_week: boolean;
          reminders: {
            day1?: boolean;
            week1?: boolean;
          };
          custom_reminders: CustomReminder[];
          last_reminder_sent: string | null;
          background_image_url: string | null;
          image_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          name: string;
          event_date: string;
          emoji?: string;
          color?: string;
          category?: string;
          is_recurring?: boolean;
          is_featured?: boolean;
          share_id?: string | null;
          remind_1_day?: boolean;
          remind_1_week?: boolean;
          reminders?: any;
          custom_reminders?: CustomReminder[];
          last_reminder_sent?: string | null;
          background_image_url?: string | null;
          image_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          name?: string;
          event_date?: string;
          emoji?: string;
          color?: string;
          category?: string;
          is_recurring?: boolean;
          is_featured?: boolean;
          share_id?: string | null;
          remind_1_day?: boolean;
          remind_1_week?: boolean;
          reminders?: any;
          custom_reminders?: CustomReminder[];
          last_reminder_sent?: string | null;
          background_image_url?: string | null;
          image_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      user_subscriptions: {
        Row: {
          id: string;
          user_id: string;
          is_pro: boolean;
          stripe_customer_id: string | null;
          stripe_subscription_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          is_pro?: boolean;
          stripe_customer_id?: string | null;
          stripe_subscription_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          is_pro?: boolean;
          stripe_customer_id?: string | null;
          stripe_subscription_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
    };
  };
}

export interface CustomReminder {
  id: string;
  value: number;
  unit: 'minutes' | 'hours' | 'days' | 'weeks';
}

export type Countdown = Database['public']['Tables']['countdowns']['Row'];
export type Subscription = Database['public']['Tables']['user_subscriptions']['Row'];

export const CATEGORIES = ['Birthday', 'Holiday', 'Travel', 'School', 'Personal', 'Work', 'Other'] as const;
export type Category = typeof CATEGORIES[number];

export const COLORS = ['pink', 'yellow', 'sage', 'mint', 'lavender', 'blush', 'blue'] as const;
export type Color = typeof COLORS[number];

export const COLOR_STYLES = {
  pink: {
    card: 'bg-[#fce7f3]',
    text: 'text-[#9d174d]',
    label: 'text-[#9d174d]/50',
    badge: 'bg-[#fbcfe8] text-[#9d174d]',
    badgeText: 'text-[#9d174d]',
    accent: '#ec4899'
  },
  yellow: {
    card: 'bg-[#fef9c3]',
    text: 'text-[#854d0e]',
    label: 'text-[#854d0e]/50',
    badge: 'bg-[#fef08a] text-[#854d0e]',
    badgeText: 'text-[#854d0e]',
    accent: '#f59e0b'
  },
  sage: {
    card: 'bg-[#d1fae5]',
    text: 'text-[#065f46]',
    label: 'text-[#065f46]/50',
    badge: 'bg-[#a7f3d0] text-[#065f46]',
    badgeText: 'text-[#065f46]',
    accent: '#10b981'
  },
  mint: {
    card: 'bg-[#ecfdf5]',
    text: 'text-[#065f46]',
    label: 'text-[#065f46]/50',
    badge: 'bg-[#d1fae5] text-[#065f46]',
    badgeText: 'text-[#065f46]',
    accent: '#10b981'
  },
  lavender: {
    card: 'bg-[#f3e8ff]',
    text: 'text-[#6b21a8]',
    label: 'text-[#6b21a8]/50',
    badge: 'bg-[#e9d5ff] text-[#6b21a8]',
    badgeText: 'text-[#6b21a8]',
    accent: '#a855f7'
  },
  blush: {
    card: 'bg-[#fff1f2]',
    text: 'text-[#9f1239]',
    label: 'text-[#9f1239]/50',
    badge: 'bg-[#ffe4e6] text-[#9f1239]',
    badgeText: 'text-[#9f1239]',
    accent: '#f43f5e'
  },
  blue: {
    card: 'bg-[#e0f2fe]',
    text: 'text-[#075985]',
    label: 'text-[#075985]/50',
    badge: 'bg-[#bae6fd] text-[#075985]',
    badgeText: 'text-[#075985]',
    accent: '#0ea5e9'
  }
};

export const CATEGORY_COLOR_MAP: Record<string, keyof typeof COLOR_STYLES> = {
  'Birthday': 'pink',
  'Holiday': 'yellow',
  'Travel': 'sage',
  'School': 'lavender',
  'Personal': 'blush',
  'Work': 'blue',
  'Other': 'blue'
};